import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from '../../components/ui.js';
import { dirtySave, useDirtyFlag } from './useSettingsDraft.js';

/**
 * Якщо в розділі є незбережені налаштування, перехід на іншу вкладку
 * не відбувається мовчки: питаємо, застосувати зміни чи піти без них.
 */
export function UnsavedLeave(): ReactNode {
  const dirty = useDirtyFlag(s => s.dirty);
  const busy = useDirtyFlag(s => s.busy);
  const navigate = useNavigate();
  const location = useLocation();
  const here = location.pathname + location.search + location.hash;
  const [to, setTo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!dirty) {
      setTo(null);
      return;
    }
    const onClick = (e: MouseEvent): void => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement).closest('a[href]');
      if (!(a instanceof HTMLAnchorElement) || a.target === '_blank' || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const next = url.pathname + url.search + url.hash;
      if (next === here) return;
      if (next === '/settings' && here.startsWith('/settings')) return;
      if (next === '/program' && here.startsWith('/program')) return;
      e.preventDefault();
      e.stopPropagation();
      setTo(next);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirty, here]);

  if (!to) return null;

  const go = (): void => {
    const next = to;
    setTo(null);
    navigate(next);
  };

  const apply = async (): Promise<void> => {
    setSaving(true);
    try {
      if (await dirtySave()) go();
    } finally {
      setSaving(false);
    }
  };

  const pending = saving || busy;

  return (
    <Modal
      title="Незбережені налаштування"
      onClose={() => setTo(null)}
      width={440}
      footer={
        <>
          <button className="btn" onClick={go}>
            Продовжити перехід
          </button>
          <button className="btn primary" disabled={pending} onClick={() => void apply()}>
            {pending ? 'Зберігаю…' : 'Застосувати налаштування'}
          </button>
        </>
      }
    >
      <p style={{ margin: 0 }}>
        Зміни в цьому розділі ще не збережено. Застосувати їх і перейти далі чи продовжити без збереження?
      </p>
    </Modal>
  );
}
